import LocalizedTextField from '@components/Customization/LocalizedTextField';
import { addButtonStyles } from '@components/Customization/PoiDesigner/designer.styles';
import { listTextLimits } from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { Box, Button, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import {
  reportDesignLimits, reportFieldSources, type ReportFieldItem, type ReportFieldSource, type ReportSignatureLine,
} from '@store/api/reportDesignEndpoints';
import { arrayMove } from '@dnd-kit/sortable';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from 'react-line-awesome';
import { fieldSourceLabels } from './blockCatalog';
import { listRowStyles } from './designer.styles';

/** The longest id a metadata entry is named by: the request schema's own limit. */
const METADATA_KEY_LENGTH = 64;

interface RowActionsProps {
  index: number;
  count: number;
  disabled?: boolean;
  onMove: (to: number) => void;
  onRemove: () => void;
}

/** Up, down and remove for one row of a list. */
const RowActions: React.FC<RowActionsProps> = ({ index, count, disabled, onMove, onRemove }) => {
  const { t } = useLocalization();

  const button = (title: string, icon: React.ReactNode, onClick: () => void, off: boolean) => (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" disabled={disabled || off} onClick={onClick} sx={{ color: 'text.secondary' }}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Stack direction="row" sx={{ flexShrink: 0 }}>
      {button(t('Customization_Designer_MoveUp'), <ArrowUpIcon />, () => onMove(index - 1), index === 0)}
      {button(t('Customization_Designer_MoveDown'), <ArrowDownIcon />, () => onMove(index + 1), index === count - 1)}
      {button(t('Common_Delete'), <TrashIcon />, onRemove, false)}
    </Stack>
  );
};

interface FieldItemsProps {
  items: ReportFieldItem[];
  onChange: (items: ReportFieldItem[]) => void;
  disabled?: boolean;
}

/**
 * The values a `fields` block prints, in order: what each one reads, and the label over it
 * where the source's own name will not do.
 */
export const FieldItemsEditor: React.FC<FieldItemsProps> = ({ items, onChange, disabled }) => {
  const { t } = useLocalization();

  const replace = (index: number, item: ReportFieldItem) =>
    onChange(items.map((current, at) => (at === index ? item : current)));

  // Only a metadata item names an entry: the key goes with the source that reads it.
  const withSource = ({ label }: ReportFieldItem, source: ReportFieldSource): ReportFieldItem =>
    ({ source, ...(label ? { label } : {}) });

  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('ReportDesign_Field_Items')}</Typography>

      {items.map((item, index) => (
        <Stack key={index} sx={listRowStyles}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              fullWidth
              size="small"
              label={t('ReportDesign_Field_Source')}
              value={item.source}
              onChange={(event) => replace(index, withSource(item, event.target.value as ReportFieldSource))}
              disabled={disabled}
            >
              {reportFieldSources.map((source) => (
                <MenuItem key={source} value={source}>{t(fieldSourceLabels[source])}</MenuItem>
              ))}
            </TextField>
            <RowActions
              index={index}
              count={items.length}
              disabled={disabled}
              onMove={(to) => onChange(arrayMove(items, index, to))}
              onRemove={() => onChange(items.filter((_, at) => at !== index))}
            />
          </Stack>

          {item.source === 'metadata' && (
            <TextField
              fullWidth
              size="small"
              label={t('ReportDesign_Field_MetadataKey')}
              helperText={t('ReportDesign_Field_MetadataKey_Help')}
              value={item.metadataKey ?? ''}
              onChange={(event) => replace(index, { ...item, metadataKey: event.target.value })}
              slotProps={{ htmlInput: { maxLength: METADATA_KEY_LENGTH } }}
              disabled={disabled}
            />
          )}

          <LocalizedTextField
            optional
            carriedHint={t('ReportDesign_Text_OtherLanguage')}
            label={t('Customization_Label')}
            // What is printed over the value while this is empty.
            placeholder={item.source === 'metadata' ? item.metadataKey : t(fieldSourceLabels[item.source])}
            value={item.label ?? {}}
            onChange={(label) => replace(index, { ...item, label })}
            maxLength={listTextLimits.fieldLabel}
            disabled={disabled}
          />
        </Stack>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<PlusIcon />}
          sx={addButtonStyles}
          disabled={disabled || items.length >= reportDesignLimits.fieldItems}
          onClick={() => onChange([...items, { source: 'projectName' }])}
        >
          {t('ReportDesign_Items_Add')}
        </Button>
      </Box>
    </Stack>
  );
};

interface SignatureLinesProps {
  /** Absent while the block prints its two default lines. */
  lines: ReportSignatureLine[] | undefined;
  /** `undefined` goes back to the default lines. */
  onChange: (lines: ReportSignatureLine[] | undefined) => void;
  disabled?: boolean;
}

/**
 * The lines of a `signatures` block. With none of its own the block prints the report's two
 * default lines, in the reader's language, so the list starts empty and says so; the first
 * line added replaces both.
 */
export const SignatureLinesEditor: React.FC<SignatureLinesProps> = ({ lines, onChange, disabled }) => {
  const { t } = useLocalization();
  const own = lines ?? [];

  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('ReportDesign_Field_Lines')}</Typography>

      {lines === undefined && (
        <Typography variant="caption" color="text.secondary">
          {t('ReportDesign_Lines_Default', [t('Report_Signature_InspectedBy'), t('Report_Signature_ApprovedBy')])}
        </Typography>
      )}

      {own.map((signature, index) => (
        <Stack key={index} direction="row" spacing={1} alignItems="flex-start" sx={listRowStyles}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LocalizedTextField
              optional
              carriedHint={t('ReportDesign_Text_OtherLanguage')}
              label={t('Customization_Label')}
              value={signature.label}
              onChange={(label) => onChange(own.map((current, at) => (at === index ? { ...current, label } : current)))}
              maxLength={listTextLimits.signatureLabel}
              disabled={disabled}
            />
          </Box>
          <RowActions
            index={index}
            count={own.length}
            disabled={disabled}
            onMove={(to) => onChange(arrayMove(own, index, to))}
            // A block with no line of its own prints the default ones again.
            onRemove={() => onChange(own.length > 1 ? own.filter((_, at) => at !== index) : undefined)}
          />
        </Stack>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<PlusIcon />}
          sx={addButtonStyles}
          disabled={disabled || own.length >= reportDesignLimits.signatureLines}
          onClick={() => onChange([...own, { label: {} }])}
        >
          {t('ReportDesign_Lines_Add')}
        </Button>
      </Box>
    </Stack>
  );
};
